<template>
  <WdsStack justify-space-between class="tw-mx-1 tw-pb-1.5">
    <div>
      <WdsStack justify-start gap-sm align-center>
        <WdsInput
          type="text"
          class="tw-min-w-[320px]"
          default
          prefix-icon="search"
          :placeholder="$t('Search recruitment agency')"
          data-test="tc-search-input"
          @update:model-value="(value) => $emit('search', value)"
        />

        <WdsButtonGhost
          class="tw-h-7.5"
          :class="{ 'filters-active': filtersAreOpened }"
          data-test="tc-filter-button"
          @click="filtersAreOpened = !filtersAreOpened"
        >
          <div class="tw-my-auto tw-px-3">
            <WdsStack gap-sm align-center>
              <WdsIcon name="settings-adjust" size="16" />
              <WdsText>
                {{ $t('Filter') }}
              </WdsText>
            </WdsStack>
          </div>
        </WdsButtonGhost>
      </WdsStack>

      <WdsStack v-if="filtersAreOpened" gap-md class="tw-mt-4">
        <WdsSelect
          v-model="filters.statuses"
          can-deselect
          searchable
          multi-select
          :list="filterOptions.statuses"
          :placeholder="$t('Status')"
          class="tw-w-[200px]"
          data-test="recruiter-filter-status"
        />

        <WdsSelect
          v-model="filters.tags"
          can-deselect
          searchable
          multi-select
          :list="filterOptions.tags"
          :placeholder="$t('Tags')"
          class="tw-w-[200px]"
          data-test="recruiter-filter-tags"
        />

        <WdsSelect
          v-model="filters.markets"
          can-deselect
          searchable
          multi-select
          :list="filterOptions.markets"
          :placeholder="$t('Market')"
          class="tw-w-[200px]"
          data-test="recruiter-filter-markets"
        />

        <WdsButtonGhost
          v-if="displayResetFiltersButton"
          secondary
          class="tw-h-[38px]"
          data-test="tc-filter-reset"
          @click="resetFilters"
        >
          <div class="tw-my-auto">
            {{ $t('Reset filters') }}
          </div>
        </WdsButtonGhost>
      </WdsStack>
    </div>
    <div>
      <WdsSelect
        :list="pageCountOptions"
        :start-selected="defaultPageLength"
        data-test="tc-page-count"
        @select-option="(newValue) => $emit('update-page-length', newValue.value)"
      ></WdsSelect>
    </div>
  </WdsStack>
</template>

<script setup>
import { ref, defineEmits, watch, computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import {
  WdsInput,
  WdsButtonGhost,
  WdsIcon,
  WdsText,
  WdsStack,
  WdsSelect,
} from '@worksome/wds-components'
import orderBy from 'lodash/orderBy'
import uniqBy from 'lodash/uniqBy'
import upperFirst from 'lodash/upperFirst'
import { $t } from '@/composables/translation'
import { PER_PAGE_MAXIMUM } from '@/utils/constants'
import {
  RecruitersFilterDataQuery,
} from '@/components/Contacts/CompanyRecruiter/queries/Recruiters'

const props = defineProps({
  defaultPageLength: { type: Number, default: 0 },
  defaultPageLengthOptions: { type: Array, default: () => [10, 50, 200] },
})

const emit = defineEmits(['update-page-length', 'search', 'update-filters'])

const query = computed(() => RecruitersFilterDataQuery)

const queryVariables = ref({
  first: PER_PAGE_MAXIMUM,
  accounts: [currentAccount.globalId],
})

const { result } = useQuery(query, queryVariables, {
  clientId: 'publicApi',
})

// Format filter options in the structure expected by the Select component
const filterOptions = computed(() => {
  const options = result.value?.recruitersFilterData?.data?.reduce(
    (main, recruiter) => ({
      ...main,

      statuses: {
        ...main.statuses,
        [recruiter.id]: {
          label: $t(upperFirst(recruiter.status.toLowerCase())),
          value: recruiter.status,
        },
      },

      tags: {
        ...main.tags,
        ...recruiter.tags.map((tag) => ({
          label: tag.name,
          value: tag.name,
        })),
      },

      markets: {
        ...main.markets,
        [recruiter.id]: {
          label: recruiter.recruiter.market.name,
          value: recruiter.recruiter.market.code,
        },
      },
    }),
    { statuses: [], tags: [], markets: [] },
  )


  const statuses = uniqBy(Object.values(options.statuses), (status) => status.value)
  const tags = uniqBy(Object.values(options.tags), (tag) => tag.value)
  const markets = uniqBy(Object.values(options.markets), (tag) => tag.value)

  return {
    statuses: orderBy(statuses, 'label', 'asc'),
    tags: orderBy(tags, 'label', 'asc'),
    markets: orderBy(markets, 'label', 'asc'),
  }
})

const pageCountOptions = props.defaultPageLengthOptions.map((value) => ({
  value,
  label: `${value}`,
}))

const filtersAreOpened = ref(false)

const filters = ref({
  accounts: [{ value: currentAccount.globalId }],
  statuses: [],
  tags: [],
  markets: [],
})

watch(filters.value, (updatedFilters) => {
  emit('update-filters', {
    accounts: updatedFilters.accounts.map(({ value }) => value),
    statuses: updatedFilters.statuses.map(({ value }) => value),
    tags: updatedFilters.tags.map(({ value }) => value),
    markets: updatedFilters.markets.map(({ value }) => value),
  })
})

const displayResetFiltersButton = computed(() => {
  const { accounts, ...rest } = filters.value

  return Object.values(rest).flat().length > 0
})

const defaultAccountFilter = computed(() =>
  [{ value: currentAccount.globalId }]
)

const resetFilters = () => {
  filters.value.accounts = defaultAccountFilter.value
  filters.value.statuses = []
  filters.value.tags = []
  filters.value.markets = []
}
</script>

<style scoped lang="scss">
:deep(.wds-input-label) {
  margin-bottom: 0 !important;
}

.trusted-contact-filter {
  padding: 0;
}

.wds-button-ghost.filters-active {
  @apply tw-bg-true-gray-300;
}
</style>
